import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';

const PrivacyPolicy: React.FC = () => {
  usePageTitle('Privacy Policy');

  return (
    <div className="legal-page">
      <div className="legal-container">
        <header className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="legal-effective-date">Effective Date: March 19, 2026</p>
        </header>

        <main className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Patriot Consulting Technology Group, LLC ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Patriot Academy Premium online training platform, including our website and related services.
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Information You Provide Directly</h3>
            <p>We collect information you voluntarily provide, including:</p>
            <ul className="legal-list">
              <li><strong>Account Information:</strong> Name, email address, phone number, company name, and job title</li>
              <li><strong>Billing Information:</strong> Billing address and payment method details (note: credit card data is processed and stored securely by Intuit/QuickBooks, not by us)</li>
              <li><strong>Enrollment Data:</strong> Course selections, enrollment dates, completion status, and performance metrics</li>
              <li><strong>Communication Data:</strong> Messages, inquiries, and support requests you send to us</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <p>We collect certain information automatically when you use our platform:</p>
            <ul className="legal-list">
              <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on platform, clicks, and interactions</li>
              <li><strong>Authentication Tokens:</strong> Minimal cookies for session management and authentication</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <p>
              We may receive information from Microsoft Entra ID when you authenticate through your Microsoft account, including your Microsoft identity and organizational affiliation.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="legal-list">
              <li><strong>Course Delivery:</strong> Enrolling you in courses, delivering content via Teams webinars, and tracking progress</li>
              <li><strong>Payment Processing:</strong> Processing course payments and maintaining billing records through QuickBooks Online</li>
              <li><strong>Communications:</strong> Sending course updates, confirmations, reminders, and responding to inquiries</li>
              <li><strong>Account Management:</strong> Creating and maintaining your account, resetting passwords, and managing access</li>
              <li><strong>Improvement:</strong> Analyzing platform usage to enhance functionality, user experience, and course content</li>
              <li><strong>Legal Compliance:</strong> Fulfilling legal obligations and enforcing our terms of service</li>
              <li><strong>Marketing (with consent):</strong> Sending promotional materials and updates about new courses (you may opt out at any time)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Third-Party Services and Data Sharing</h2>
            <p>
              We use trusted third-party services to deliver and support our platform. Your data is shared with these providers only to the extent necessary:
            </p>

            <h3>4.1 Microsoft Services</h3>
            <ul className="legal-list">
              <li><strong>Microsoft Entra ID:</strong> For authentication and identity management</li>
              <li><strong>Microsoft Teams:</strong> For webinar delivery and course sessions</li>
              <li><strong>Azure Static Web Apps:</strong> For hosting and platform infrastructure</li>
            </ul>

            <h3>4.2 Intuit/QuickBooks Online</h3>
            <ul className="legal-list">
              <li><strong>Payment Processing:</strong> Intuit securely processes and stores payment information. We never handle or store credit card data directly.</li>
            </ul>

            <h3>4.3 Other Disclosures</h3>
            <p>
              We do not sell, rent, or trade your personal information to third parties for marketing purposes. We may disclose information when required by law or to protect our legal rights.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Retention</h2>
            <ul className="legal-list">
              <li><strong>Enrollment Records:</strong> Retained for 3 years after course completion for certification verification and audit purposes</li>
              <li><strong>Payment Records:</strong> Retained in accordance with QuickBooks Online retention policies (typically 7 years for tax/accounting purposes)</li>
              <li><strong>Account Information:</strong> Retained while your account is active. Upon account deletion request, personal data is removed within 30 days, except as required by law</li>
              <li><strong>Authentication Tokens:</strong> Session tokens expire automatically; no persistent tracking cookies are used</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Your Privacy Rights</h2>

            <h3>6.1 Access and Correction</h3>
            <p>
              You have the right to access, update, or correct your personal information. You can manage much of this information directly through your account dashboard, or contact us for assistance.
            </p>

            <h3>6.2 Deletion Requests</h3>
            <p>
              You may request deletion of your account and associated personal data. We will delete your information within 30 days, except where we must retain it for legal, tax, or contractual reasons. Enrollment and payment records may be retained for the periods specified in Section 5.
            </p>

            <h3>6.3 Data Portability</h3>
            <p>
              You may request a copy of your personal data in a portable format. Contact us at the information below to submit such a request.
            </p>

            <h3>6.4 Opt-Out of Marketing</h3>
            <p>
              You may opt out of promotional communications at any time by clicking the "unsubscribe" link in emails or by contacting us directly.
            </p>

            <h3>6.5 California and European Residents</h3>
            <p>
              If you are a California resident (CCPA) or European resident (GDPR), you have additional rights. Please contact us with specific requests, and we will comply with applicable laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Security Measures</h2>
            <p>
              We implement comprehensive security measures to protect your information:
            </p>
            <ul className="legal-list">
              <li><strong>Encryption:</strong> All data in transit is encrypted using TLS (Transport Layer Security)</li>
              <li><strong>Secure Hosting:</strong> Platform is hosted on Azure Static Web Apps with enterprise-grade security</li>
              <li><strong>No Direct Card Storage:</strong> Credit card information is never stored by us; Intuit handles all payment data securely</li>
              <li><strong>Access Controls:</strong> Strict authentication and authorization controls limit data access to authorized personnel</li>
              <li><strong>Regular Updates:</strong> We maintain current security patches and protocols</li>
            </ul>
            <p>
              While we strive to protect your information, no security system is impenetrable. We cannot guarantee absolute security but commit to industry-standard practices.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Cookies and Tracking</h2>
            <p>
              We use minimal cookies for essential platform functionality only:
            </p>
            <ul className="legal-list">
              <li><strong>Session Cookies:</strong> Maintain your login session (expire when you close your browser)</li>
              <li><strong>Authentication Tokens:</strong> Secure token-based authentication for Microsoft Entra ID integration</li>
              <li><strong>No Tracking Cookies:</strong> We do not use third-party tracking or analytics cookies for profiling</li>
            </ul>
            <p>
              You may control cookies through your browser settings. Disabling essential cookies may impact platform functionality.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Patriot Academy Premium is not directed to children under 13. We do not knowingly collect information from children. If we become aware that a child under 13 has provided information, we will delete such data promptly. If you believe a child has used our service, please contact us immediately.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of material changes by posting the updated policy on our website with a new effective date. Your continued use of the platform constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have questions, concerns, or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="contact-info">
              <p>
                <strong>Patriot Consulting Technology Group, LLC</strong><br />
                17192 Murphy Avenue, #14067<br />
                Irvine, CA 92623
              </p>
              <p>
                <strong>Email:</strong> <a href="mailto:training@patriotconsulting.com">training@patriotconsulting.com</a><br />
                <strong>Phone:</strong> <a href="tel:1-844-560-4630">1-844-560-4630</a><br />
                <strong>Website:</strong> <a href="https://patriotconsulting.com" target="_blank" rel="noopener noreferrer">patriotconsulting.com</a>
              </p>
            </div>
            <p>
              We will respond to inquiries and requests within 30 days or as required by law.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
